import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApiClient, Datenblatt, Feldvorschlag, LueftungFeld } from '../../api';
import { inDieAblage } from '../../kopieren';
import { IconDatei, IconHochladen } from '../../Icons';
import {
  ContentSection, DataTable, Drawer, ErrorState, LoadingState, PropertyGrid, StatusBadge, formatiereZahl, type Spalte,
} from '../../muster';
import { datenblattStatus, feldBeschriftung, gueteStatus, inArbeit, kb } from './bestandregeln';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * HERSTELLERUNTERLAGEN UND GERÄTEDATENBLATT - DIE ABLAGE- UND ÜBERNAHMEWEGE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * TV6c: aus `seiten/Lueftung.tsx` herausgelöst und auf die Muster gesetzt.
 * Jeder Aufruf ist der alte, mit denselben Argumenten (Charakterisierung
 * `Lueftung.schreibwege.test.tsx`): `datenblattFinden`, `datenblattDatei`,
 * `datenblattUebernehmen` (zählen, dann übernehmen), `datenblattDateiVerteilen`.
 */

/**
 * Ein Ablagefeld für eine Datei: ziehen oder klicken. Nimmt nur die Endung an (Vorgabe PDF); sonst meldet es
 * `falsch`. TV6d: Endung, Dateiauswahl und Meldung einstellbar (JSON der Herstellerauslegung, Anlagenliste).
 */
export function Ablagefeld({
  beschriftung, laeuft, ablegen, fehlerMelden, endung = '.pdf', akzeptiert = '.pdf', falsch = 'Nur PDF-Dateien.',
  laeuftText = 'wird abgelegt …',
}: {
  beschriftung: string;
  laeuft: boolean;
  ablegen: (datei: File) => void;
  fehlerMelden: (text: string) => void;
  endung?: string;
  akzeptiert?: string;
  falsch?: string;
  laeuftText?: string;
}) {
  const [ueberZiel, setUeberZiel] = useState(false);
  const pruefen = (datei: File | undefined) => {
    if (!datei) return;
    if (!datei.name.toLowerCase().endsWith(endung)) { fehlerMelden(falsch); return; }
    ablegen(datei);
  };
  return (
    <label
      className={`ablegefeld ${ueberZiel ? 'bereit' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setUeberZiel(true); }}
      onDragLeave={() => setUeberZiel(false)}
      onDrop={(e) => { e.preventDefault(); setUeberZiel(false); pruefen(e.dataTransfer.files?.[0]); }}
    >
      <IconHochladen size={16} />
      <span>{laeuft ? laeuftText : beschriftung}</span>
      <input
        type="file"
        accept={akzeptiert}
        className="lue-verborgen"
        disabled={laeuft}
        onChange={(e) => { const d = e.target.files?.[0]; e.target.value = ''; pruefen(d); }}
      />
    </label>
  );
}

/** Ein Verweis mit „öffnen“ und „Link kopieren“ - der eigene, signierte Verweis, nicht der des Objektspeichers. */
export function Verweis({ verweis, fehlerMelden }: { verweis: string; fehlerMelden?: (text: string) => void }) {
  const [kopiert, setKopiert] = useState(false);
  return (
    <span className="lue-verweis">
      <a href={verweis} target="_blank" rel="noreferrer"><IconDatei size={13} /> Datenblatt öffnen</a>
      <button
        type="button"
        className="m-knopf"
        onClick={async () => {
          if (await inDieAblage(verweis)) {
            setKopiert(true);
            setTimeout(() => setKopiert(false), 2000);
          } else {
            fehlerMelden?.('Der Verweis liess sich nicht kopieren. Er steht im Feld „Link zu Herstellerunterlagen".');
          }
        }}
      >
        {kopiert ? 'kopiert' : 'Link kopieren'}
      </button>
      <span className="lue-leise">Ohne Anmeldung erreichbar - derselbe Verweis steht im Modell.</span>
    </span>
  );
}

// ═══ Herstellerunterlagen eines Bauteiltyps ════════════════════════════════════
//
// Der Datensatz hängt an Familie und Typ, nicht am Bauteil - 826 Brandschutz-
// klappen teilen sich eine Handvoll Größen. Was hier geschieht, wirkt deshalb
// auf alle Bauteile desselben Typs; das steht dabei.

export function Herstellerunterlagen({
  api, familie, typ, bauart, projektId, bauteilId, felder, datenblattUrl,
}: {
  api: ApiClient;
  familie: string;
  typ: string;
  bauart: string | null;
  projektId: string;
  bauteilId: string;
  /** Der Feldkatalog dieser Bauart - für die Beschriftung im Schubfach. */
  felder: LueftungFeld[];
  /** Der gleichbleibende Verweis aus dem Bauteilfeld (auch wenn das PDF über die Seite Herstellerunterlagen kam). */
  datenblattUrl: string | null;
}) {
  const [blatt, setBlatt] = useState<Datenblatt | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [pruefen, setPruefen] = useState(false);

  // Mit Projektkennung: Ohne sie sucht der Endpunkt nach ProjectId IS NULL und legte ein zweites Datenblatt an.
  const laden = useCallback(async () => {
    try {
      setBlatt(await api.datenblattFinden(familie, typ, projektId));
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [api, familie, typ, projektId]);

  useEffect(() => { setBlatt(null); setMeldung(null); void laden(); }, [laden]);

  // Solange ausgewertet wird, nachladen - die Auswertung braucht rund eine Minute.
  useEffect(() => {
    if (!inArbeit(blatt?.zustand)) return undefined;
    const takt = setInterval(() => { void laden(); }, 15_000);
    return () => clearInterval(takt);
  }, [blatt?.zustand, laden]);

  const hochladen = async (datei: File) => {
    if (!blatt) return;
    setLaeuft(true);
    setFehler(null);
    setMeldung(null);
    try {
      const a = await api.datenblattDatei(blatt.id, datei);
      setBlatt(a.blatt);
      setMeldung(a.verweis
        ? `${datei.name} abgelegt, Verweis an ${a.bauteile} Bauteil(e) geschrieben.`
        : `${datei.name} abgelegt, aber der Verweis liess sich nicht bilden - `
          + 'Aps__CallbackBaseUrl oder Aps__CallbackSigningKey fehlt.');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  if (!blatt) {
    return (
      <ContentSection titel="Herstellerunterlagen">
        {fehler
          ? <ErrorState klein meldung="Die Herstellerunterlagen konnten nicht abgerufen werden." technisch={fehler} beiErneutVersuchen={() => { void laden(); }} />
          : <LoadingState text="Herstellerunterlagen werden abgerufen …" zeilen={3} />}
      </ContentSection>
    );
  }

  return (
    <ContentSection
      titel="Herstellerunterlagen"
      meta={<StatusBadge {...datenblattStatus(blatt.zustand)} />}
      beschreibung={`Gilt für alle Bauteile vom Typ ${typ}${bauart ? ` (${bauart})` : ''} - das Datenblatt gehört zum Typ, nicht zum einzelnen Bauteil.`}
    >
      <PropertyGrid
        dicht
        eintraege={[
          { beschriftung: 'Datenblatt', wert: blatt.hatDatei ? `${blatt.dateiname ?? ''} · ${kb(blatt.dateiGroesse)}` : 'keines abgelegt' },
          {
            beschriftung: 'Hersteller',
            wert: blatt.herstellerUrl
              ? <a href={blatt.herstellerUrl} target="_blank" rel="noreferrer">{blatt.hersteller ?? 'Herstellerseite'}</a>
              : (blatt.hersteller ?? '—'),
          },
          { beschriftung: 'Vorschläge', wert: blatt.hatVorschlaege ? 'liegen vor' : 'keine' },
        ]}
      />

      <Ablagefeld
        beschriftung={blatt.hatDatei ? 'Anderes Datenblatt hierher ziehen oder klicken' : 'Datenblatt hierher ziehen oder klicken'}
        laeuft={laeuft}
        ablegen={(d) => { void hochladen(d); }}
        fehlerMelden={setFehler}
      />
      <p className="lue-leise">
        Das vollständige Blatt genügt. Es wird nicht ausgewertet, sondern als Unterlage verlinkt - die technischen Werte
        kommen über die JSON-Zuordnung.
      </p>

      {datenblattUrl && <Verweis verweis={datenblattUrl} fehlerMelden={setFehler} />}

      {fehler && <ErrorState klein meldung={fehler} />}
      <p className="lue-meldung" role="status">{meldung ?? (blatt.zustandHinweis || '')}</p>

      {blatt.hatVorschlaege && (
        <button type="button" className="m-knopf m-knopf-haupt" onClick={() => setPruefen(true)}>Vorschläge prüfen</button>
      )}

      {/* Verteilen erst mit Datei: Ein TROX-Blatt deckt die Baureihe ab (RN-D/80, /100, /160 sind drei Typen, ein PDF). */}
      {blatt.hatDatei && <Verteilen api={api} blatt={blatt} projektId={projektId} neuLaden={() => { void laden(); }} />}

      {pruefen && (
        <Vorschlagsdialog
          api={api}
          blatt={blatt}
          projektId={projektId}
          bauteilId={bauteilId}
          felder={felder}
          schliessen={() => { setPruefen(false); void laden(); }}
        />
      )}
    </ContentSection>
  );
}

// ═══ Vorschläge prüfen und übernehmen (Schubfach) ═══════════════════════════════
//
// Der Teil, der nicht ersetzbar ist: Ein Mensch liest neben jedem Wert die
// Belegstelle im Wortlaut. Vorangehakt wird nur „hoch“; „mittel“, „unsicher“
// und eine Auswahl werden bewusst angehakt - bei fünfzig Feldern der
// Unterschied zwischen einer Prüfung und einem Durchwinken.

function Vorschlagsdialog({ api, blatt, projektId, bauteilId, felder, schliessen }: {
  api: ApiClient;
  blatt: Datenblatt;
  projektId: string;
  bauteilId: string;
  felder: LueftungFeld[];
  schliessen: () => void;
}) {
  const vorschlaege = useMemo<Feldvorschlag[]>(() => (blatt.vorschlaegeJson ? JSON.parse(blatt.vorschlaegeJson) : []), [blatt.vorschlaegeJson]);
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(
    () => new Set(vorschlaege.filter((v) => v.guete === 'hoch').map((v) => v.feldSchluessel)));
  // Der gewählte Wert je Feld - bei einer Auswahl kann er von der Vorgabe abweichen.
  const [werte, setWerte] = useState<Record<string, string>>(
    () => Object.fromEntries(vorschlaege.map((v) => [v.feldSchluessel, v.wert])));
  const [alleDesTyps, setAlleDesTyps] = useState(true);
  const [ueberschreiben, setUeberschreiben] = useState(false);
  const [zahl, setZahl] = useState<number | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState<string | null>(null);

  const umschalten = (schluessel: string) => {
    setGewaehlt((s) => {
      const neu = new Set(s);
      if (neu.has(schluessel)) neu.delete(schluessel); else neu.add(schluessel);
      return neu;
    });
    setZahl(null);
  };

  const anfrage = (nurZaehlen: boolean) => ({
    projectId: projektId,
    componentId: alleDesTyps ? undefined : bauteilId,
    felder: [...gewaehlt],
    werte: Object.fromEntries([...gewaehlt].map((k) => [k, werte[k]])),
    ueberschreiben,
    nurZaehlen,
  });

  const zaehlen = async () => {
    setLaeuft(true); setFehler(null); setFertig(null);
    try {
      setZahl((await api.datenblattUebernehmen(blatt.id, anfrage(true))).betroffen);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const uebernehmen = async () => {
    setLaeuft(true); setFehler(null);
    try {
      const a = await api.datenblattUebernehmen(blatt.id, anfrage(false));
      setFertig(`${a.betroffen} Bauteil(e) geändert` + (a.uebersprungen ? `, ${a.uebersprungen} Werte übersprungen.` : '.'));
      setZahl(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const beschriftung = (schluessel: string) => {
    const f = felder.find((x) => x.fieldKey === schluessel);
    return f ? feldBeschriftung(f) : schluessel;
  };

  const spalten: Spalte<Feldvorschlag>[] = [
    {
      schluessel: 'wahl', titel: 'übernehmen', sortierbar: false, wert: (v) => gewaehlt.has(v.feldSchluessel),
      inhalt: (v) => (
        <input
          type="checkbox"
          aria-label={`${beschriftung(v.feldSchluessel)} übernehmen`}
          checked={gewaehlt.has(v.feldSchluessel)}
          onChange={() => umschalten(v.feldSchluessel)}
        />
      ),
    },
    { schluessel: 'feld', titel: 'Feld', wert: (v) => beschriftung(v.feldSchluessel), immerSichtbar: true },
    {
      schluessel: 'wert', titel: 'Wert', wert: (v) => werte[v.feldSchluessel] ?? v.wert,
      inhalt: (v) => (v.alternativen && v.alternativen.length > 1 ? (
        // Die Quelle nennt mehrere Möglichkeiten - ein Mensch wählt in einer Sekunde, was ein Modell nur raten könnte.
        <select
          className="m-feld-eingabe"
          aria-label={`Wert ${beschriftung(v.feldSchluessel)}`}
          value={werte[v.feldSchluessel] ?? v.wert}
          onChange={(e) => { setWerte((w) => ({ ...w, [v.feldSchluessel]: e.target.value })); setZahl(null); }}
        >
          {v.alternativen.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      ) : <strong>{v.wert}</strong>),
    },
    {
      schluessel: 'guete', titel: 'Güte', art: 'status', wert: (v) => gueteStatus(v.guete).text,
      inhalt: (v) => <StatusBadge {...gueteStatus(v.guete)} klein />,
    },
    { schluessel: 'quelle', titel: 'Fundstelle', wert: (v) => v.quelle ?? '' },
    { schluessel: 'beleg', titel: 'Beleg', wert: (v) => v.beleg ?? '' },
  ];

  return (
    <Drawer
      offen
      titel={`Vorschläge aus dem Datenblatt · ${blatt.typ}`}
      beschreibung="Nur Werte mit Güte „hoch“ sind vorangehakt - bei ihnen enthält die Belegstelle den Wert wörtlich. Wo „Auswahl“ steht, nennt die Unterlage mehrere Möglichkeiten: dort erst wählen, dann anhaken. Bitte die Belege lesen, bevor Sie übernehmen."
      breite="720px"
      beiSchliessen={schliessen}
      fuss={(
        <>
          <button type="button" className="m-knopf" disabled={laeuft || gewaehlt.size === 0} onClick={() => void zaehlen()}>Zählen</button>
          <button
            type="button"
            className="m-knopf m-knopf-haupt"
            disabled={laeuft || zahl === null || zahl === 0}
            onClick={() => void uebernehmen()}
          >
            {zahl !== null ? `${gewaehlt.size} Feld(er) auf ${zahl} Bauteil(e) übernehmen` : 'Erst zählen'}
          </button>
          <button type="button" className="m-knopf" onClick={schliessen}>{fertig ? 'Schließen' : 'Abbrechen'}</button>
        </>
      )}
    >
      <DataTable<Feldvorschlag>
        name="Vorschläge"
        spalten={spalten}
        zeilen={vorschlaege}
        schluessel={(v) => v.feldSchluessel}
        zeilenKlasse={(v) => (v.guete === 'unsicher' ? 'datenfeld' : undefined)}
        suche={false}
        dicht
        leer={<span>Keine Vorschläge vorhanden.</span>}
      />

      <div className="lue-haken">
        <label>
          <input type="checkbox" checked={alleDesTyps} onChange={(e) => { setAlleDesTyps(e.target.checked); setZahl(null); }} />
          {' '}Auf alle Bauteile dieses Typs anwenden, nicht nur auf dieses
        </label>
        <label>
          <input type="checkbox" checked={ueberschreiben} onChange={(e) => { setUeberschreiben(e.target.checked); setZahl(null); }} />
          {' '}Vorhandene Werte überschreiben
        </label>
        {/* Diese Zusage gehört hierhin, nicht in eine Anleitung: Ohne sie traut sich niemand, den Knopf zu drücken. */}
        <p className="lue-leise">Von Hand gepflegte Werte bleiben immer unberührt, auch mit Häkchen.</p>
      </div>

      {fehler && <ErrorState klein meldung={fehler} />}
      <p className="lue-meldung" role="status">
        {fertig ?? (zahl !== null ? `${formatiereZahl(zahl, 0)} Bauteil(e) betroffen, ${gewaehlt.size} Feld(er) je Bauteil.` : '')}
      </p>
    </Drawer>
  );
}

// ═══ Datei auf die Baureihe verteilen ═══════════════════════════════════════════
//
// Verteilt wird die Datei, nicht die Vorschläge: Die Werte je Baugröße
// unterscheiden sich, jeder Typ wird einzeln ausgewertet.

function Verteilen({ api, blatt, projektId, neuLaden }: {
  api: ApiClient;
  blatt: Datenblatt;
  projektId: string;
  neuLaden: () => void;
}) {
  const [beauftragen, setBeauftragen] = useState(true);
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const verteilen = async () => {
    setLaeuft(true); setFehler(null); setMeldung(null);
    try {
      const a = await api.datenblattDateiVerteilen(blatt.id, projektId, { ueberschreiben: false, beauftragen });
      setMeldung(a.verteilt === 0
        ? 'Zur Familie gibt es keinen weiteren Typ ohne Datei.'
        : `Auf ${a.verteilt} weitere(n) Typ(en) verteilt`
          + (a.angelegt ? `, ${a.angelegt} Datensatz/Datensätze angelegt` : '')
          + (a.uebersprungen ? `, ${a.uebersprungen} hatten schon eine Datei` : '')
          + (a.beauftragt ? `. ${a.beauftragt} Auswertung(en) eingestellt.` : '.'));
      neuLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="lue-baureihe">
      <h3 className="lue-unterkopf">Für die ganze Baureihe</h3>
      <p className="lue-leise">
        Dieses Datenblatt deckt vermutlich mehrere Baugrößen ab. Die Datei lässt sich auf alle Typen der Familie
        {' '}<strong>{blatt.familie}</strong> übernehmen - ausgewertet wird jeder Typ einzeln, weil die Werte je
        Baugröße verschieden sind.
      </p>
      <label className="lue-haken">
        <span>
          <input type="checkbox" checked={beauftragen} onChange={(e) => setBeauftragen(e.target.checked)} />
          {' '}Auswertung für die übrigen Typen gleich mit einstellen
        </span>
      </label>
      <button type="button" className="m-knopf" disabled={laeuft} onClick={() => void verteilen()}>
        Datei auf die Baureihe verteilen
      </button>
      {fehler && <ErrorState klein meldung={fehler} />}
      <p className="lue-meldung" role="status">{meldung ?? ''}</p>
    </div>
  );
}

/** Eine Unterlage der Anlage (technisches Datenblatt, Skizze) mit ihrem Verweis. */
export function Unterlage({ titel, dateiname, groesse, vorhanden, verweis }: {
  titel: string;
  dateiname: string | null;
  groesse: number | null;
  vorhanden: boolean;
  verweis: string | null;
}) {
  return (
    <div className="lue-unterlage">
      <h3 className="lue-unterkopf">{titel}</h3>
      {vorhanden ? (
        <>
          <p className="lue-leise">{dateiname}{groesse !== null ? ` (${kb(groesse)})` : ''}</p>
          {verweis
            ? <Verweis verweis={verweis} />
            : <p className="lue-leise">Kein Verweis hinterlegt - beim nächsten Hochladen entsteht er.</p>}
        </>
      ) : <p className="lue-leise">nicht abgelegt</p>}
    </div>
  );
}
